import { useEffect, useMemo, useRef, useState } from "react";

import { isSafari } from "react-device-detect";
import ReactPlayer from "react-player";
import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";
import LightBoxItem from "@/components/ui/lightbox/light-box-item";
import { resolveVideoPoster } from "@/components/ui/lightbox/utils";
import { resolveAttachmentFileExtension } from "@/libs/file-helper";
import { BwPlayFill } from "../icons";

/**
 * ReactPlayer falls back to its OWN default play button when `playIcon` is
 * null or undefined; an empty fragment is the only way to render none. The
 * custom control lives in the `light` thumbnail below instead.
 */
const HIDDEN_PLAY_ICON = <></>;

type LightBoxItemVideoProps<
  TAttachment extends LightBoxAttachment = LightBoxAttachment,
> = {
  item: TAttachment;
  onProgress?: (state: any) => void;
  open?: boolean;
};

const LightBoxItemVideo = ({
  item: attachment,
  onProgress,
  open,
}: LightBoxItemVideoProps) => {
  const [playing, setPlaying] = useState(attachment?.autoplay);
  const hideThumbnail = useRef<boolean>(false);
  // The still the browser could not paint (a video whose poster is not written
  // yet): drop the preview rather than show the broken-image glyph — the native
  // player renders the first frame itself.
  const [posterFailed, setPosterFailed] = useState(false);
  const poster = resolveVideoPoster(attachment);
  const handleOnVideoProgress = (state: any) => {
    onProgress?.(state);
  };

  // Support for member portal, todo: remove this after member portal is implemented
  const contentUri = useMemo(() => {
    const fileExtension = resolveAttachmentFileExtension({
      fileName: attachment?.fileName,
      contentUri: attachment?.contentUri,
      contentType: attachment?.contentType,
    });
    if (
      (fileExtension === "avi" || fileExtension === "mkv") &&
      isSafari &&
      attachment?.contentUri?.indexOf("stream=true") === -1
    ) {
      const url = new URL(attachment?.contentUri as string);
      const params = new URLSearchParams(url.search);
      params.append("stream", "true");
      url.search = params.toString();
      return url.toString();
    }
    return attachment?.contentUri;
  }, [attachment]);

  const showThumbnail = useMemo(() => {
    //when autoplay not show thumbnail
    if (attachment?.autoplay) return false;
    return attachment?.showThumbnail || true;
  }, [attachment]);

  useEffect(() => {
    if (!open) {
      setPlaying(false);
    }
  }, [open]);

  return (
    <LightBoxItem item={attachment}>
      <div className="w-screen">
        <div className="w-full max-w-[58.5rem] mx-auto">
          <div className="relative pt-[56.25%]">
            <ReactPlayer
              key={attachment?.contentUri}
              src={contentUri || ""}
              controls
              playing={playing}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              width="100%"
              height="100%"
              className="absolute inset-0"
              onProgress={handleOnVideoProgress}
              playIcon={HIDDEN_PLAY_ICON}
              onClickPreview={() => {
                hideThumbnail.current = true;
                setPlaying(true);
              }}
              light={
                showThumbnail &&
                !hideThumbnail.current &&
                poster &&
                !posterFailed ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={poster}
                      alt="Thumbnail"
                      className="w-full h-full object-contain"
                      onError={() => setPosterFailed(true)}
                    />
                    <div className="absolute size-16 flex items-center justify-center rounded-full bg-bg-dark-min">
                      <BwPlayFill className="text-text-light" size={32} />
                    </div>
                  </div>
                ) : (
                  false
                )
              }
            />
          </div>
        </div>
      </div>
    </LightBoxItem>
  );
};

export default LightBoxItemVideo;
